import {
  describeEarlyRepayment,
  EARLY_REPAYMENT_TYPE_LABELS,
  EarlyRepaymentType,
  EarlyRepaymentUnit,
  type IEarlyRepayment,
} from "@krea/shared/features/early-repayment";

const feeText = (
  unit: EarlyRepaymentUnit,
  value: number,
  formatCurrency: (n: number) => string,
): string =>
  unit === EarlyRepaymentUnit.PERCENT
    ? `${value}% of the remaining principal`
    : formatCurrency(value);

export interface IEarlyRepaymentLabel {
  /** The agreed terms; "—" when nothing is known. */
  label: string;
  /** For a separate labelled box; never folded into `label`. */
  comment: string | null;
}

/**
 * The lender app has no i18n in its bid views (supportedLngs is ["en"] and the
 * bid forms/previews use literal strings), so this returns plain strings.
 * The customer side maps the same descriptor through `t()` instead.
 */
export const earlyRepaymentLabel = (
  terms: IEarlyRepayment,
  formatCurrency: (n: number) => string,
): IEarlyRepaymentLabel => {
  const { summary, comment } = describeEarlyRepayment(terms);

  let base: string | null;
  switch (summary.kind) {
    case "free":
      base = EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FREE];
      break;
    case "feeApplies":
      base = feeText(summary.unit, summary.value, formatCurrency);
      break;
    case "freeAfterPeriod": {
      // D2: the fee applies DURING the period, not after it.
      const monthsUnit = summary.months === 1 ? "month" : "months";
      base = `${feeText(summary.unit, summary.value, formatCurrency)} for the first ${summary.months} ${monthsUnit}, then free`;
      break;
    }
    case "fullInterestAndFees":
      base =
        EARLY_REPAYMENT_TYPE_LABELS[EarlyRepaymentType.FULL_INTEREST_AND_FEES];
      break;
    // Migrated bid: type known, numbers missing. Name the terms anyway.
    case "typeOnly":
      base = EARLY_REPAYMENT_TYPE_LABELS[summary.type];
      break;
    default:
      base = null;
  }

  // An untyped bid with only a comment reads "—": the comment is never
  // promoted into the value.
  return { label: base ?? "—", comment };
};
